import { ReactNode, RefObject, useCallback, useId } from 'react';
import { useTooltipTriggerState } from 'react-stately';
import { mergeProps, usePress, useTooltipTrigger, VisuallyHidden } from 'react-aria';
import { DOMAttributes } from '@react-types/shared';
import { isMobile } from '@direct-frontend/platform-code';

import { TooltipProps } from '../../components/Tooltip';

const TOOLTIP_AUTO_FOCUS_PROPS = { autoFocus: false, contain: false, restoreFocus: false };

export interface UseItemTooltipParams {
    /**
     * Реф элемента-триггера
     */
    triggerRef: RefObject<HTMLElement>;

    /**
     * Признак наличия тултипа у элемента
     */
    hasTooltip: boolean;

    /**
     * Признак отключения элемента
     */
    isDisabled?: boolean;

    /**
     * Текст тултипа для скринридеров: привязывается к триггеру постоянным aria-describedby,
     * чтобы его было слышно без наведения
     */
    description?: string;
}

export interface UseItemTooltipResult {
    /**
     * Пропсы элемента-триггера
     */
    triggerProps: DOMAttributes;

    /**
     * Пропсы компонента тултипа
     */
    tooltipProps: Pick<TooltipProps, 'isOpen' | 'onClose' | 'onMouseEnter' | 'onMouseLeave' | 'autoFocusProps'>;

    /**
     * Описание для скринридеров
     */
    descriptionNode: ReactNode;
}

export function useItemTooltip({
    triggerRef,
    hasTooltip,
    isDisabled,
    description,
}: UseItemTooltipParams): UseItemTooltipResult {
    const descriptionId = useId();
    const state = useTooltipTriggerState({ delay: 0, closeDelay: 0 });
    const { triggerProps } = useTooltipTrigger({ isDisabled: !hasTooltip || isMobile() }, state, triggerRef);

    // useTooltipTrigger при открытии проставляет aria-describedby со своим внутренним
    // id тултипа, но Popup рендерит собственный id — ссылка вела бы в никуда.
    // Выкидываем её и привязываем триггер только к descriptionNode
    const { 'aria-describedby': _unboundDescribedBy, ...boundTriggerProps } = triggerProps;

    const openTooltip = useCallback(() => state.open(true), [state]);
    const closeTooltip = useCallback(() => state.close(), [state]);

    const { pressProps } = usePress({
        ref: triggerRef,
        /**
         * TODO DIRECT-331705 Открытие по тапу на задизейбленный элемент плохо с точки зрения доступности,
         * временное решение
         */
        isDisabled: !hasTooltip || !isMobile() || !isDisabled,
        onPress: openTooltip,
    });

    const withDescription = Boolean(description) && hasTooltip;

    return {
        triggerProps: {
            ...mergeProps(boundTriggerProps, isMobile() ? pressProps : {}),
            'aria-describedby': withDescription ? descriptionId : undefined,
        },
        tooltipProps: {
            isOpen: state.isOpen,
            onClose: state.close,
            onMouseEnter: openTooltip,
            onMouseLeave: closeTooltip,
            autoFocusProps: TOOLTIP_AUTO_FOCUS_PROPS,
        },
        descriptionNode: withDescription ? (
            <VisuallyHidden>
                <span id={descriptionId}>{description}</span>
            </VisuallyHidden>
        ) : null,
    };
}
